import React, { useState, useEffect } from 'react';
import { domainService } from '../../services';
import { Domain, DomainCreate, DomainUpdate } from '../../types';
import { Modal } from '../ui';

interface Props {
  domain: Domain | null;  // null = new domain
  onClose: () => void;
  onSave: () => void;
}

export const DomainFormModal: React.FC<Props> = ({ domain, onClose, onSave }) => {
  const isEdit = !!domain;
  const [formData, setFormData] = useState({
    code: '',
    name: '',
    description: '',
    is_active: true
  });
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');

  useEffect(() => {
    if (domain) {
      setFormData({
        code: domain.name,  // name is actually the code
        name: domain.display_name || domain.name,
        description: domain.description || '',
        is_active: domain.is_active
      });
    } else {
      setFormData({
        code: '',
        name: '',
        description: '',
        is_active: true
      });
    }
  }, [domain]);

  const handleCodeChange = (value: string) => {
    // Auto-format code: lowercase, replace spaces with underscores, remove special chars
    const formatted = value.toLowerCase().replace(/[^a-z0-9_]/g, '_').replace(/_+/g, '_');
    setFormData({ ...formData, code: formatted });
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setSaving(true);
    setError('');

    try {
      if (isEdit) {
        const updateData: DomainUpdate = {
          name: formData.name,
          description: formData.description || undefined,
          is_active: formData.is_active
        };
        await domainService.update(domain.name, updateData);
      } else {
        const createData: DomainCreate = {
          code: formData.code,
          name: formData.name,
          description: formData.description || undefined,
          is_active: formData.is_active
        };
        await domainService.create(createData);
      }
      onSave();
    } catch (err: any) {
      setError(err.response?.data?.detail || err.message || 'Erreur lors de la sauvegarde');
    } finally {
      setSaving(false);
    }
  };

  return (
    <Modal
      isOpen={true}
      onClose={onClose}
      title={isEdit ? 'Modifier le domaine' : 'Nouveau domaine'}
      size="sm"
    >
      {error && (
        <div className="bg-red-100 text-red-700 p-3 rounded mb-4">{error}</div>
      )}

      <form onSubmit={handleSubmit}>
        <div className="space-y-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Code technique
            </label>
            <input
              type="text"
              value={formData.code}
              onChange={(e) => handleCodeChange(e.target.value)}
              disabled={isEdit}
              className="w-full border rounded px-3 py-2 disabled:bg-gray-100 disabled:text-gray-500"
              placeholder="ex: acte_vente"
              required
            />
            {isEdit && (
              <p className="text-xs text-gray-500 mt-1">Le code ne peut pas être modifié</p>
            )}
            {!isEdit && (
              <p className="text-xs text-gray-500 mt-1">Lettres minuscules, chiffres et underscores uniquement</p>
            )}
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Nom d'affichage
            </label>
            <input
              type="text"
              value={formData.name}
              onChange={(e) => setFormData({ ...formData, name: e.target.value })}
              className="w-full border rounded px-3 py-2"
              placeholder="ex: Acte de Vente"
              required
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Description
            </label>
            <textarea
              value={formData.description}
              onChange={(e) => setFormData({ ...formData, description: e.target.value })}
              className="w-full border rounded px-3 py-2"
              rows={3}
              placeholder="Description optionnelle du domaine..."
            />
          </div>

          <div className="flex items-center gap-2">
            <input
              type="checkbox"
              id="is_active"
              checked={formData.is_active}
              onChange={(e) => setFormData({ ...formData, is_active: e.target.checked })}
              className="rounded border-gray-300"
            />
            <label htmlFor="is_active" className="text-sm text-gray-700">
              Domaine actif
            </label>
          </div>
        </div>

        <div className="flex justify-end gap-3 mt-6 pt-4 border-t">
          <button
            type="button"
            onClick={onClose}
            className="px-4 py-2 border rounded hover:bg-gray-50"
          >
            Annuler
          </button>
          <button
            type="submit"
            disabled={saving}
            className="px-4 py-2 bg-primary text-white rounded hover:bg-primary-600 disabled:opacity-50"
          >
            {saving ? 'Sauvegarde...' : (isEdit ? 'Modifier' : 'Créer')}
          </button>
        </div>
      </form>
    </Modal>
  );
};

export default DomainFormModal;
